import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Repeat, Plus, Trash2, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

export default function PaymentTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formName, setFormName] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDesc, setFormDesc] = useState('');

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data.templates);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName || !formAmount) return;
    setSaving(true);
    try {
      await api.post('/templates', { name: formName, amount: parseFloat(formAmount), description: formDesc });
      toast.success('Template created');
      setShowAdd(false);
      setFormName(''); setFormAmount(''); setFormDesc('');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/templates/${deleteTarget.template_id}`);
      toast.success('Template deleted');
      setDeleteTarget(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

  const applyTemplate = (tpl) => {
    navigate('/payment', { state: { amount: tpl.amount, description: tpl.description || tpl.name } });
  };

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Payment Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-16 text-center">
            <Repeat className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No templates yet. Create quick-use payment templates.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-grid">
          {templates.map(tpl => (
            <div key={tpl.template_id} className="bg-card border border-border p-4 group" data-testid={`template-${tpl.template_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground truncate block">{tpl.name}</span>
                  {tpl.description && <span className="text-xs text-muted-foreground truncate block mt-0.5">{tpl.description}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(tpl)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-template-${tpl.template_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="text-xl font-bold tabular-nums mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(tpl.amount)}
              </div>
              <Button onClick={() => applyTemplate(tpl)} className="w-full h-9 bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm" data-testid={`use-template-${tpl.template_id}`}>
                <Zap className="w-3.5 h-3.5 mr-1.5" /> Use Template
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>New Payment Template</DialogTitle>
            <DialogDescription>Create a reusable template for common payment amounts.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Standard consultation" required className="h-11" data-testid="template-name-input" /></div>
            <div className="space-y-2"><Label>Amount (EUR) *</Label><Input type="number" step="0.01" min="0.01" value={formAmount} onChange={e => setFormAmount(e.target.value)} required className="h-11" data-testid="template-amount-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Optional note" className="h-11" data-testid="template-desc-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-template-dialog">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>Remove "{deleteTarget?.name}" template?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-template-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
